import { type FC } from 'react'
import { observer } from 'mobx-react-lite'
import {
  Button,
  DropdownCard,
  type IIconsVector,
  type IDropdownItem,
  type ITooltipProps,
} from 'shared/ui'
import { uiStore } from 'shared/store/uiStore'
import { type IFiltersListGroups, type IContactsDataParams } from 'entities/Contacts/api/types'
import { BroadcastsRoutes } from 'entities/ExportRouters'

type IBroadcastSendButtonProps = {
  contactsData: IContactsDataParams
  variant: 'dropdownItem' | 'button'
  onClick?: () => void
}

export const BroadcastSendButton: FC<IBroadcastSendButtonProps> = observer(
  ({ contactsData, variant, onClick }) => {
    const text = 'Send Broadcast'
    const icon: IIconsVector = 'broadcasts'
    const { selectedIds, bulkAll, contactsParams, search } = contactsData

    const disabledTooltipProps: ITooltipProps = {
      label: (
        <>
          Send Broadcast disabled with manual search
          <br />+ select all. Clear search or use filters instead.
        </>
      ),
      placement: 'top',
    }

    const disabled = !!search && bulkAll

    const handleClick = () => {
      if (disabled) return

      const filterListGroups: IFiltersListGroups = {}

      if (contactsParams?.filtersList && bulkAll) {
        filterListGroups.advanced = [{ filtersList: contactsParams.filtersList }]
      }

      // Use number is_known filter as all selected w/o filters
      if (!contactsParams?.filtersList?.length && bulkAll) {
        filterListGroups.advanced = [
          {
            filtersList: [
              {
                filters: [
                  {
                    key: 'number',
                    operator: 'is_known',
                    value: [],
                  },
                ],
                custom_filters: [],
                filter_tags: [],
              },
            ],
          },
        ]
      }

      onClick?.()
      uiStore.changeRoute({
        path: `${BroadcastsRoutes.root}/${BroadcastsRoutes.create}`,
        options: {
          state: {
            contact_ids: selectedIds,
            filters_list_groups: filterListGroups.advanced
              ? JSON.stringify(filterListGroups)
              : undefined,
          },
        },
      })
    }

    if (variant === 'dropdownItem') {
      const item: IDropdownItem = {
        id: 'send_broadcast',
        iconL: icon,
        label: text,
        disabled,
        tooltipProps: disabled ? disabledTooltipProps : undefined,
      }
      return <DropdownCard item={item} onChange={handleClick} />
    }

    return (
      <Button
        text={text}
        icon={icon}
        typeBtn='contained'
        contained='tertiary'
        size='small'
        onClick={handleClick}
        disabled={disabled}
        tooltipProps={disabled ? disabledTooltipProps : undefined}
      />
    )
  }
)
